import {
  Locale,
  useConfig,
  useHostName,
  usePublicProtocol,
} from '@aboutyou/config';
import {Gallery} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/gallery.types';
import {
  ShopStreamItem,
  ShopStreamItemV2,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/brand_hub/v1/service.types';
import {CategoryStreamItem} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/stream/stream.types';
import {CategoryStreamItemV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/stream/stream_v2.types';
import {useFormatCurrency} from '@aboutyou/i18n';
import {useIsCategoryPage} from '@aboutyou/router';
import React, {useCallback} from 'react';

const getFirstGalleryImage = (gallery: Gallery) => {
  const imageGallery = gallery?.elements
    .filter(element => {
      return element.type?.$case === 'image';
    })
    .map(element => {
      return (
        element.type?.$case === 'image' ? element.type.image.image : undefined
      )!;
    });

  return imageGallery[0];
};

export const ProductStreamStructureMarkup: React.FC<{
  items:
    | CategoryStreamItem[]
    | CategoryStreamItemV2[]
    | ShopStreamItem[]
    | ShopStreamItemV2[];
}> = ({items}) => {
  const hostname = useHostName();
  const {locale} = useConfig();
  const protocol = usePublicProtocol();
  const formatCurrency = useFormatCurrency();
  const isCategoryPage = useIsCategoryPage();

  const currencyFormatOption = {
    truncateOverride: true,
    withoutCurrencySymbol: true,
    overrideDefaultLimiter: '.',
    overrideThousandsSeparator: ``,
  } as const;

  const createProductsJsonLd = useCallback(
    (
      products:
        | CategoryStreamItem[]
        | CategoryStreamItemV2[]
        | ShopStreamItem[]
        | ShopStreamItemV2[],
    ) => {
      const payload = products.reduce(
        (acc, curr) => {
          if (curr.type?.$case === 'productSection') {
            const productTile = curr.type.productSection.productTile;
            const lowPrice = productTile.price!.price.amount;
            const highPrice = productTile.price!.tracker?.fullPrice;
            const priceCurrency = productTile.price!.price?.currencyCode;

            const image = getFirstGalleryImage(productTile.gallery);

            const imageUrl = image ? new URL(image.src) : null;
            const offers = productTile.price?.reductions.length
              ? {
                  '@type': 'AggregateOffer',
                  lowPrice: formatCurrency(
                    lowPrice,
                    priceCurrency,
                    currencyFormatOption,
                  ),
                  highPrice: formatCurrency(
                    highPrice,
                    priceCurrency,
                    currencyFormatOption,
                  ),
                  priceCurrency,
                }
              : {
                  '@type': 'Offer',
                  price: formatCurrency(
                    lowPrice,
                    priceCurrency,
                    currencyFormatOption,
                  ),
                  priceCurrency,
                };

            const structuredProduct = {
              '@type': 'ListItem',
              position: curr.streamPosition + 1,
              item: {
                '@type': 'Product',
                name: productTile.productTracker.productName,
                image: imageUrl ? `${imageUrl.origin}${imageUrl.pathname}` : '',
                offers,
                url: `${protocol}://${hostname}${productTile.link.url}`,
                brand: {
                  '@type': 'Brand',
                  name: productTile.brandTracker.name,
                },
              },
            };
            return {
              ...acc,
              itemListElement: [...acc.itemListElement, structuredProduct],
            };
          }
          return acc;
        },
        {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: [] as NonNullable<unknown>[],
        },
      );
      return payload;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return locale !== Locale.hu_HU && isCategoryPage ? (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(createProductsJsonLd(items)),
      }}
    />
  ) : null;
};
